import type { AppCredentialPayload, AppCredentialValue, PushPlatform } from '@/features/push-credentials';
import type { App } from '@/services/db/app/entity';
import {
  createApp,
  deleteAppPlatform,
  linkAppPlatform,
  updateApp,
  upsertAppPlatformCredential,
} from '@/services/db/app/mutations';
import { asFcmUpload } from './platform-state';

export const SAVE_FAILED_MESSAGE = "This app couldn't be saved. Check your connection and try again.";

/**
 * Captured before the first request: the sequence awaits between calls, so
 * reading the form again part-way through could act on a control the user
 * moved in the meantime.
 */
export type SavePlan = {
  name: string;
  ios: AppCredentialValue | null;
  android: AppCredentialValue | null;
  renamed: boolean;
  disableIos: boolean;
  disableAndroid: boolean;
};

export type SaveTarget = {
  workspaceId: number;
  appId: number;
};

/**
 * The caller keeps what this returns, so a create that fails part-way retries
 * against that record rather than registering a second app.
 */
export async function ensureApp(workspaceId: number, existing: App | null, name: string): Promise<App> {
  if (existing != null) {
    return existing;
  }
  return createApp({ workspaceId, name });
}

async function saveAppDetails(target: SaveTarget, plan: SavePlan): Promise<void> {
  if (!plan.renamed) {
    return;
  }
  await updateApp({ ...target, name: plan.name });
}

async function deleteDisabledPlatforms(target: SaveTarget, plan: SavePlan): Promise<void> {
  if (plan.disableIos) {
    await deleteAppPlatform({ ...target, platform: 'ios' });
  }
  if (plan.disableAndroid) {
    await deleteAppPlatform({ ...target, platform: 'android' });
  }
}

/** Lets a save that fails later tell which platforms are already written. */
export type OnCredentialPersisted = (platform: PushPlatform) => void;

async function savePlatform(
  target: SaveTarget,
  platform: PushPlatform,
  value: AppCredentialValue | null,
  onPersisted: OnCredentialPersisted,
): Promise<void> {
  if (value == null) {
    return;
  }
  if ('credential_id' in value) {
    await linkAppPlatform({ ...target, platform, identifier: value.identifier, credential_id: value.credential_id });
  } else {
    await upsertAppPlatformCredential({ ...target, platform, ...value });
  }
  onPersisted(platform);
}

function oneSharedFcmFile(plan: SavePlan): { ios: AppCredentialPayload; android: AppCredentialValue } | null {
  const ios = asFcmUpload(plan.ios);
  const android = asFcmUpload(plan.android);
  if (ios == null || android == null || ios.credential.password !== android.credential.password) {
    return null;
  }
  return { ios, android };
}

const SHARED_CREDENTIAL_UNIDENTIFIED = 'The stored push credential came back without an id.';

async function saveCredentials(target: SaveTarget, plan: SavePlan, onPersisted: OnCredentialPersisted): Promise<void> {
  const shared = oneSharedFcmFile(plan);
  if (shared != null) {
    const response = await upsertAppPlatformCredential({ ...target, platform: 'ios', ...shared.ios });
    onPersisted('ios');
    const credentialId = response.platform.credential_id;
    // Linking Android to nothing would register a platform that cannot send,
    // and calling it written would drop it from the retry, so the save stops
    // here instead and Android stays staged.
    if (credentialId == null) {
      throw new Error(SHARED_CREDENTIAL_UNIDENTIFIED);
    }
    await linkAppPlatform({
      ...target,
      platform: 'android',
      identifier: shared.android.identifier,
      credential_id: credentialId,
    });
    onPersisted('android');
    return;
  }
  await savePlatform(target, 'ios', plan.ios, onPersisted);
  await savePlatform(target, 'android', plan.android, onPersisted);
}

/**
 * No transaction backs these calls, so the order keeps a partial failure
 * sensible: a platform is removed before its replacement is written, and no
 * credential is stored against a name the user has since changed.
 */
export async function applySavePlan(
  target: SaveTarget,
  plan: SavePlan,
  onPersisted: OnCredentialPersisted = () => {},
): Promise<void> {
  await saveAppDetails(target, plan);
  await deleteDisabledPlatforms(target, plan);
  await saveCredentials(target, plan, onPersisted);
}
